// Project execution controls (RF-OPS, RF-CFG-12/14): Start/Resume/Pause/Drain/Stop through
// POST /v1/projects/{id}/execution with expected_version. For maintainer+ Start/Resume is always
// pressable and runs the readiness validation first ("Validating…" → "Starting…"): it starts only
// when no impediment is left, otherwise a toast says so. Lower roles keep Start disabled while
// known impediments exist. A compact readiness badge (Task 12) always shows the current state and
// when it was last checked, next to "Validate now/again"; clicking it opens the assistant with the
// explanation and the fix (the per-check breakdown lives there and in Settings, never in this
// header, so it cannot misalign it). A 409 refusal shows the server's causes verbatim, and engine
// errors after a committed change are never hidden.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { ProjectSummary, ReadinessCheck } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { changeExecution, getOperation, getProjectDetail, validateProject, type ExecutionAction, type ExecutionEngineReport } from "../../api/endpoints.ts";
import type { OperationRecord, ProjectDetailResponse } from "../../api/types.ts";
import { getEffectiveConfig } from "../../api/endpoints-wave2.ts";
import { isApiError } from "../../api/client.ts";
import { useAsync, useDebouncedCallback, type AsyncState } from "../../lib/useAsync.ts";
import { useOperation } from "../../lib/useOperation.ts";
import { useProjectRole } from "../../features/wave3/common.tsx";
import { Pill } from "../../components/Status.tsx";
import { useRealtime, useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { useAssistant } from "../../features/assistant/AssistantProvider.tsx";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { TextAreaField } from "../../components/Field.tsx";
import { Menu } from "../../components/Menu.tsx";
import { Banner } from "../../components/Feedback.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ChevronDownIcon, PauseIcon, PlayIcon, RefreshIcon } from "../../components/icons.tsx";

interface Blocker {
  check: string;
  cause: string;
}

const PRIMARY: Record<ProjectSummary["state"], ExecutionAction | null> = {
  draft: "start",
  active: "pause",
  paused: "resume",
  draining: "resume",
  stopped: "start",
};

const SECONDARY: Record<ProjectSummary["state"], ExecutionAction[]> = {
  draft: ["stop"],
  active: ["drain", "stop"],
  paused: ["drain", "stop"],
  draining: ["pause", "stop"],
  stopped: [],
};

/** Polls a 202 operation with the same backoff as useOperation, until it settles (or ~2.5 min). */
export async function waitForOperation(id: string, alive: () => boolean): Promise<OperationRecord | null> {
  let last: OperationRecord | null = null;
  for (let attempt = 0; attempt < 40; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, Math.min(4000, 300 * 2 ** Math.min(attempt, 4))));
    if (!alive()) return null;
    last = await getOperation(id);
    if (last.status !== "accepted" && last.status !== "running") return last;
  }
  return last;
}

/** Mirrors the server's start/resume checks with the facts the panel can read. */
function computeBlockers(readiness: ReadinessCheck[] | undefined, impediments: string[] | undefined, engineState: string | undefined, engineDetail: string | null | undefined): Blocker[] | null {
  if (readiness === undefined || impediments === undefined) return null;
  const out: Blocker[] = [];
  for (const r of readiness) if (r.status !== "passed" && r.status !== "not_applicable") out.push({ check: r.check, cause: r.cause ?? r.status });
  for (const i of impediments) out.push({ check: "effective_config", cause: i });
  if (engineState === "unavailable") out.push({ check: "engine", cause: engineDetail ?? "unavailable" });
  return out;
}

/**
 * The most recent `checked_at` among the readiness checks (Task 12 badge and Settings card): `null` when none has
 * run yet, or when every stored timestamp fails to parse — an unparseable date is never shown as a fabricated
 * "checked —", it is treated the same as never having been checked (fix round 1, minor).
 */
export function newestCheckedAt(checks: ReadinessCheck[]): string | null {
  let newest: string | null = null;
  for (const c of checks) {
    if (c.checked_at === null || Number.isNaN(Date.parse(c.checked_at))) continue;
    if (newest === null || c.checked_at > newest) newest = c.checked_at;
  }
  return newest;
}

export function ExecutionControls({
  project,
  onChanged,
  detail,
}: {
  project: ProjectSummary;
  onChanged: () => void;
  /** Lifted to the page (fix round 1, minor): one fetch shared with the Settings readiness card, one realtime
   * subscription keeps both in sync — `ExecutionControls` still owns reloading it after its own validate/start
   * flows via `detail.setData`/`detail.reload()`. */
  detail: AsyncState<ProjectDetailResponse>;
}) {
  const { t, formatRelative } = useI18n();
  const { toast } = useToast();
  const { health } = useRealtime();
  const assistant = useAssistant();
  // 403/404 on the effective configuration keeps the decision with the server (never a guess).
  const effective = useAsync((s) => getEffectiveConfig(project.id, s).catch(() => null), [project.id, project.version]);
  const reloadFacts = useDebouncedCallback(() => effective.reload(), 500);
  useRealtimeEvents((e) => {
    if (e.type === "routing.config.activated" || e.type === "project.validation.completed" || e.type === "agent.profile.activated") reloadFacts();
  }, project.id);

  // "Validate now" (maintainer+, like POST /v1/projects/{id}/validate): 202 + operation polling,
  // then the stored readiness is reloaded; the Start button follows the recorded facts.
  const canValidate = useProjectRole(project.id).atLeast("maintainer");
  const validation = useOperation();
  const validationStatus = validation.op?.status;
  useEffect(() => {
    if (validationStatus === "succeeded") {
      detail.reload();
      effective.reload();
      toast(t("project.validateDone"), "success");
    } else if (validationStatus === "failed") detail.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationStatus]);

  const [busy, setBusy] = useState<ExecutionAction | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [engineErrors, setEngineErrors] = useState<ExecutionEngineReport["errors"]>([]);
  const [confirm, setConfirm] = useState<ExecutionAction | null>(null);
  const [reason, setReason] = useState("");
  // Start/Resume for maintainer+: validate first, then start only if nothing is left to fix.
  const [phase, setPhase] = useState<"validating" | "starting" | null>(null);
  const [startValidationError, setStartValidationError] = useState<string | null>(null);
  const [triedStart, setTriedStart] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    // Reset on (re)mount: StrictMode runs mount → unmount → mount in development.
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const engine = (health?.components ?? []).find((c) => c.name === "execution_engine");
  const blockers = computeBlockers(
    detail.error ? undefined : detail.data?.readiness,
    effective.data === null ? [] : effective.data?.impediments,
    engine?.state,
    engine?.detail,
  );
  // Unknown facts (still loading, or unreadable) do not disable Start: the server decides.
  const startBlocked = blockers !== null && blockers.length > 0;
  const readinessChecks = detail.error ? [] : (detail.data?.readiness ?? []);

  const primary = PRIMARY[project.state];
  const secondary = SECONDARY[project.state];

  const perform = async (action: ExecutionAction, why?: string, expectedVersion = project.version) => {
    setBusy(action);
    setRefusal(null);
    setEngineErrors([]);
    try {
      const result = await changeExecution(project.id, action, expectedVersion, why);
      toast(t(`project.execution.${action}Requested`), "success");
      if (result.engine && result.engine.errors.length > 0) setEngineErrors(result.engine.errors);
      onChanged();
    } catch (e) {
      if (isApiError(e) && e.code === "version_conflict") {
        toast(t("project.versionConflict"), "error");
        onChanged();
      }
      setRefusal(e);
      reloadFacts();
    } finally {
      setBusy(null);
    }
  };

  /**
   * Start/Resume for maintainer+: POST validate → poll the operation → re-read readiness and the
   * effective configuration → start only when no impediment is left; otherwise open the panel
   * with every failing check and its cause. The server still decides on the start itself.
   */
  const validateThenStart = async (action: ExecutionAction) => {
    setPhase("validating");
    setRefusal(null);
    setEngineErrors([]);
    setStartValidationError(null);
    setTriedStart(true);
    validation.reset();
    try {
      const accepted = await validateProject(project.id);
      let op: OperationRecord | null = null;
      if (accepted.status === "accepted" || accepted.status === "running") op = await waitForOperation(accepted.operation_id, () => alive.current);
      if (!alive.current) return;
      if (op && op.status !== "succeeded") {
        setStartValidationError(op.error?.message ?? (op.status === "failed" ? t("operation.failed") : t("project.validateStillRunning")));
        detail.reload();
        toast(t("project.startNotPossible"), "error");
        return;
      }
      const [freshDetail, freshEffective] = await Promise.all([getProjectDetail(project.id), getEffectiveConfig(project.id).catch(() => null)]);
      if (!alive.current) return;
      detail.setData(() => freshDetail);
      effective.setData(() => freshEffective);
      const left = computeBlockers(freshDetail.readiness ?? [], freshEffective === null ? [] : freshEffective.impediments, engine?.state, engine?.detail) ?? [];
      if (left.length > 0) {
        toast(t("project.startBlockedToast", { n: left.length }), "error");
        return;
      }
      setPhase("starting");
      await perform(action, undefined, freshDetail.version);
    } catch (e) {
      if (!alive.current) return;
      setRefusal(e);
      reloadFacts();
    } finally {
      if (alive.current) setPhase(null);
    }
  };

  const request = (action: ExecutionAction) => {
    if (action === "stop" || action === "drain") {
      setReason("");
      setConfirm(action);
    } else if ((action === "start" || action === "resume") && canValidate) void validateThenStart(action);
    else void perform(action);
  };

  const starting = primary === "start" || primary === "resume";
  // Maintainers can always press Start (it validates first); others follow the known facts.
  const primaryDisabled = primary === null || (starting && startBlocked && !canValidate);
  const primaryLabel = phase === "validating" ? t("project.validating") : phase === "starting" ? t("project.starting") : primary ? t(`project.${primary}`) : "";
  // Task 12: a compact badge replaces the disclosure that used to misalign the header — the per-check
  // breakdown lives in the assistant panel (badge click) and in Settings, never inlined here. A project
  // never validated is never "Ready" just because it happens to carry zero known blockers (matches the
  // backend's readinessDiagnosis: zero checks is its own "never checked" state, not "ready").
  const showReadiness = starting && blockers !== null;
  const readinessReady = blockers !== null && blockers.length === 0 && readinessChecks.length > 0;
  const checkedAt = newestCheckedAt(readinessChecks);
  const impedimentCount = blockers?.length ?? 0;
  // R20: the badge always shows when it was checked, "Ready" included. A `checkedAt` of `null` while ready (every
  // check is unparseable/missing a timestamp — a data anomaly, not "never validated") falls back to plain "Ready"
  // rather than fabricate a date.
  const readinessLabel =
    checkedAt !== null
      ? readinessReady
        ? t("project.readinessBadge.readyChecked", { when: formatRelative(checkedAt) })
        : t(impedimentCount === 1 ? "project.readinessBadge.notReadyChecked.one" : "project.readinessBadge.notReadyChecked", { n: impedimentCount, when: formatRelative(checkedAt) })
      : readinessReady
        ? t("project.readinessBadge.ready")
        : t(impedimentCount === 1 ? "project.readinessBadge.notReadyNever.one" : "project.readinessBadge.notReadyNever", { n: impedimentCount });

  return (
    <div className="stack execution-controls" style={{ gap: 6, alignItems: "flex-end" }}>
      <div className="row row--wrap" style={{ justifyContent: "flex-end" }}>
        {primary ? (
          <Button
            variant={starting ? "primary" : "secondary"}
            icon={starting ? <PlayIcon size={16} /> : <PauseIcon size={16} />}
            onClick={() => request(primary)}
            loading={busy === primary || phase !== null}
            disabled={primaryDisabled || busy !== null || phase !== null}
            aria-describedby={showReadiness ? "readiness-status" : undefined}
            data-testid="execution-primary"
            data-phase={phase ?? undefined}
          >
            {primaryLabel}
          </Button>
        ) : null}
        {secondary.length > 0 ? (
          <Menu
            label={t("project.moreExecution")}
            items={secondary.map((a) => ({ id: a, label: t(`project.${a}`), hint: t(`project.${a}Hint`), onSelect: () => request(a) }))}
            trigger={(props) => (
              <Button {...props} iconEnd={<ChevronDownIcon size={14} />} disabled={busy !== null || phase !== null}>
                {t("project.moreExecution")}
              </Button>
            )}
          />
        ) : null}
      </div>
      {showReadiness ? (
        <div className="stack readiness-status" id="readiness-row" data-testid="readiness-row">
          <div aria-live="polite">
            {validation.error ? <ActionError error={validation.error} onDismiss={validation.reset} /> : null}
            {validation.op?.status === "failed" || startValidationError ? (
              <Banner tone="error" role="alert">
                {t("project.validateFailed", { message: startValidationError ?? validation.op?.error?.message ?? t("operation.failed") })}
              </Banner>
            ) : null}
          </div>
          <div className="row row--wrap readiness-row">
            {canValidate ? (
              <Button
                size="sm"
                icon={<RefreshIcon size={14} />}
                onClick={() => {
                  setStartValidationError(null);
                  void validation.run(() => validateProject(project.id));
                }}
                loading={validation.busy}
                disabled={validation.busy || phase !== null}
                data-testid="validate-now"
              >
                {triedStart ? t("project.validateAgain") : t("project.validateNow")}
              </Button>
            ) : null}
            <button
              type="button"
              className="readiness-badge-btn"
              aria-label={t("project.readinessBadge.openAssistant", { status: readinessLabel })}
              onClick={() => assistant.open({ focus: "readiness", prompt: t("assistant.prompt.readiness") })}
              data-testid="readiness-badge"
            >
              <Pill tone={readinessReady ? "success" : "danger"} icon={readinessReady ? "check" : "x"}>
                <span id="readiness-status">{readinessLabel}</span>
              </Pill>
            </button>
          </div>
        </div>
      ) : null}
      {refusal ? (
        <div className="execution-refusal">
          <ActionError
            error={refusal}
            title={isApiError(refusal) && refusal.code === "gate_pending" ? t("project.executionRefused") : undefined}
            onDismiss={() => setRefusal(null)}
            extra={
              <Link to={`/projects/${project.id}/settings`} className="link-subtle">
                {t("project.openReadiness")}
              </Link>
            }
          />
        </div>
      ) : null}
      {engineErrors.length > 0 ? (
        <div className="execution-refusal">
          <Banner tone="warning" role="alert" title={t("project.engineErrors")}>
            <ul className="plain-list" data-testid="engine-errors">
              {engineErrors.map((e, i) => (
                <li key={i}>
                  <span className="badge mono">{e.target}</span> <span className="user-content">{e.message}</span>
                </li>
              ))}
            </ul>
          </Banner>
        </div>
      ) : null}
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm ? t(`project.${confirm}ConfirmTitle` as "project.stopConfirmTitle") : ""}
        description={confirm ? t(`project.${confirm}Hint` as "project.stopHint") : undefined}
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
            <Button
              variant={confirm === "stop" ? "danger" : "primary"}
              onClick={() => {
                const a = confirm;
                setConfirm(null);
                if (a) void perform(a, reason.trim() || undefined);
              }}
            >
              {confirm ? t(`project.${confirm}`) : ""}
            </Button>
          </>
        }
      >
        <TextAreaField label={t("kanban.reason")} optionalLabel={t("common.optional")} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000} />
      </Dialog>
    </div>
  );
}
